import { Component, Input, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Version, VersionFeature } from 'src/app/core/interfaces/version.interface';
import { LocalizationService } from 'src/app/shared/services/localization.service';

@Component({
  selector: 'app-version-details-modal',
  templateUrl: './version-details-modal.component.html',
  styleUrls: ['./version-details-modal.component.scss']
})
export class VersionDetailsModalComponent {
  @Input() version!: Version;

  private localizationService = inject(LocalizationService);

  constructor(
    public activeModal: NgbActiveModal,
    private translateService: TranslateService
  ) { }

  close(): void {
    this.activeModal.close();
  }

  private getCurrentLang(): string {
    return this.localizationService.getCachedLanguage() || 'en';
  }

  getName(): string {
    const lang = this.getCurrentLang();
    return lang === 'ar' ? (this.version.nameAr || this.version.nameEn) : (this.version.nameEn || this.version.nameAr);
  }

  getDescription(): string {
    const lang = this.getCurrentLang();
    return lang === 'ar' ? (this.version.descriptionAr || this.version.descriptionEn || '') : (this.version.descriptionEn || this.version.descriptionAr || '');
  }

  getReleaseNotes(): string {
    const lang = this.getCurrentLang();
    return lang === 'ar' ? (this.version.releaseNotesAr || this.version.releaseNotesEn || '') : (this.version.releaseNotesEn || this.version.releaseNotesAr || '');
  }

  getFeatureName(feature: VersionFeature): string {
    if (!feature) return '';
    const lang = this.getCurrentLang();
    return lang === 'ar' ? (feature.nameAr || feature.nameEn || '') : (feature.nameEn || feature.nameAr || '');
  }

  getFeatureDescription(feature: VersionFeature): string {
    if (!feature) return '';
    const lang = this.getCurrentLang();
    return lang === 'ar' ? (feature.descriptionAr || feature.descriptionEn || '') : (feature.descriptionEn || feature.descriptionAr || '');
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const locale = this.getCurrentLang() === 'ar' ? 'ar' : 'en';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  getStatusClass(): string {
    const status = this.version.status;
    if (status === 'Released' || status === 'Completed') return 'status-completed';
    if (status === 'InReview' || status === 'Approved') return 'status-in-progress';
    if (status === 'Draft') return 'status-planned';
    if (status === 'Archived') return 'status-archived';
    return 'status-default';
  }

  getStatusLabel(): string {
    const status = this.version.status;
    if (status === 'Draft') return this.translateService.instant('VERSIONS.STATUS.DRAFT');
    if (status === 'InReview') return this.translateService.instant('VERSIONS.STATUS.IN_REVIEW');
    if (status === 'Approved') return this.translateService.instant('VERSIONS.STATUS.APPROVED');
    if (status === 'Released' || status === 'Completed') return this.translateService.instant('VERSIONS.STATUS.RELEASED');
    if (status === 'Archived') return this.translateService.instant('VERSIONS.STATUS.ARCHIVED');
    return status; // Fallback to raw status value
  }
}


